import React, { useCallback, useEffect, useRef } from "react";
import { Circle, GoogleMap, Marker } from "@react-google-maps/api";

// Embedded map for choosing a hub's centre point. Clicking the map or dragging
// the pin calls onPick(lat, lon); the parent puts those numbers in the inputs.
// The circle shows the vicinity radius, i.e. the area that gets the package price.
// The Google Maps script is loaded once by GoogleMapsScript in the parent card;
// mapStatus ("loading" | "ready" | "error") says how that went.

const MAP_CONTAINER_STYLE = { width: "100%", height: "280px" };
const MAP_OPTIONS = {
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  clickableIcons: false, // a click on a shop icon should still move the pin
};
const RADIUS_CIRCLE_OPTIONS = {
  strokeColor: "#0d6efd",
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: "#0d6efd",
  fillOpacity: 0.12,
  clickable: false, // let clicks inside the circle reach the map
};
const INITIAL_ZOOM = 12; // the whole 3km circle fits in the 280px-high map
const TOKYO_STATION = { lat: 35.6812, lng: 139.7671 }; // only used if the hub has no point

// 6 decimals is about 10cm, plenty for a pickup point and easy to read.
const roundCoordinate = (value) => Math.round(value * 1e6) / 1e6;

const HubLocationMap = ({ mapStatus, latitude, longitude, radiusMeters, onPick, isDisabled }) => {
  const mapRef = useRef(null);

  const hasPosition = Number.isFinite(latitude) && Number.isFinite(longitude);
  const position = hasPosition ? { lat: latitude, lng: longitude } : null;

  // The map is only centred once, on load. After that we pan to the pin when the
  // point changes (typed or clicked). Feeding a `center` prop on every render
  // would fight the admin while they drag the map around.
  const initialCenterRef = useRef(position || TOKYO_STATION);
  useEffect(() => {
    if (mapRef.current && position) mapRef.current.panTo(position);
  }, [latitude, longitude]); // eslint-disable-line react-hooks/exhaustive-deps

  const rememberMap = useCallback((map) => {
    mapRef.current = map;
  }, []);
  const forgetMap = useCallback(() => {
    mapRef.current = null;
  }, []);

  const pickFromMapEvent = (event) => {
    if (isDisabled || !event?.latLng) return;
    onPick(roundCoordinate(event.latLng.lat()), roundCoordinate(event.latLng.lng()));
  };

  if (mapStatus === "error") {
    return (
      <div className="alert alert-warning tw:mb-0 tw:py-2 tw:text-sm" role="alert">
        The map could not be loaded. You can still type the latitude and longitude below.
      </div>
    );
  }

  if (mapStatus !== "ready") {
    return (
      <div
        className="tw:flex tw:items-center tw:justify-center tw:rounded tw:bg-gray-100 tw:text-sm tw:text-gray-500"
        style={MAP_CONTAINER_STYLE}
      >
        Loading map...
      </div>
    );
  }

  return (
    <div className="tw:overflow-hidden tw:rounded tw:border tw:border-gray-200">
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={initialCenterRef.current}
        zoom={INITIAL_ZOOM}
        options={{ ...MAP_OPTIONS, draggableCursor: isDisabled ? "default" : "crosshair" }}
        onLoad={rememberMap}
        onUnmount={forgetMap}
        onClick={pickFromMapEvent}
      >
        {position && (
          <>
            <Marker position={position} draggable={!isDisabled} onDragEnd={pickFromMapEvent} />
            <Circle center={position} radius={radiusMeters} options={RADIUS_CIRCLE_OPTIONS} />
          </>
        )}
      </GoogleMap>
    </div>
  );
};

export default HubLocationMap;
